import React, {useEffect, useMemo, useState} from 'react'
import {Badge, Button, Col, Form, Modal, Row} from 'react-bootstrap'
import {useSelector} from 'react-redux'
import moment from 'moment-timezone'
import {
  ContactStatus,
  CreateTaskPayload,
  TaskItem,
  TaskLinkedContact,
  TaskLinkedContactField,
  TaskPriority,
} from '../../../../../services/cellcast.v2.types'
import QMContactListPopup from '../../../../Components/QMContactListPopup'
import './TaskFormModal.scss'

export type TaskFormValues = CreateTaskPayload

type TaskFormModalProps = {
  show: boolean
  onClose: () => void
  onSubmit: (values: TaskFormValues) => Promise<void>
  isSubmitting?: boolean
  initialTask?: TaskItem | null
}

const PRIORITY_OPTIONS: TaskPriority[] = ['low', 'medium', 'high']

type SelectedContact = {
  id: string
  label: string
}

const resolveIanaTimeZone = (candidate?: string): string => {
  const trimmed = typeof candidate === 'string' ? candidate.trim() : ''
  if (trimmed && moment.tz.zone(trimmed)) return trimmed
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

const toDateTimeLocalValueInTz = (isoDate?: string, timeZone?: string): string => {
  if (!isoDate) return ''
  const m = moment(isoDate)
  if (!m.isValid()) return ''
  const zone = resolveIanaTimeZone(timeZone)
  return m.tz(zone).format('YYYY-MM-DDTHH:mm')
}

const parseDateTimeLocalParts = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
  if (!match) return null

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  }
}

const getTimeZoneOffsetMinutes = (timestamp: number, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })

  const parts = formatter.formatToParts(new Date(timestamp))
  const map = parts.reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value
    }
    return acc
  }, {})

  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  )

  return (asUtc - timestamp) / 60000
}

const toUtcIsoFromTimeZone = (dateTimeLocal: string, timeZone?: string): string => {
  const fallback = new Date(dateTimeLocal)
  if (!timeZone) return Number.isNaN(fallback.getTime()) ? '' : fallback.toISOString()

  const parts = parseDateTimeLocalParts(dateTimeLocal)
  if (!parts) return Number.isNaN(fallback.getTime()) ? '' : fallback.toISOString()

  try {
    let utcTs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0)
    const firstOffset = getTimeZoneOffsetMinutes(utcTs, timeZone)
    utcTs -= firstOffset * 60000

    const secondOffset = getTimeZoneOffsetMinutes(utcTs, timeZone)
    if (firstOffset !== secondOffset) {
      utcTs -= (secondOffset - firstOffset) * 60000
    }

    return new Date(utcTs).toISOString()
  } catch (error) {
    return Number.isNaN(fallback.getTime()) ? '' : fallback.toISOString()
  }
}

const buildNameContact = (contact: TaskLinkedContact) => {
  const fullName =
    contact?.fullName ||
    contact.fields?.find((field: TaskLinkedContactField) => field.name === 'Full Name')?.value ||
    contact.fields?.find((field: TaskLinkedContactField) => field.name === 'First Name')?.value ||
    contact.fields?.find((field: TaskLinkedContactField) => field.name === 'Last Name')?.value

  const trimmedName = fullName?.trim()
  return trimmedName || contact.number || ''
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
  show,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialTask,
}) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [dueDateUtc, setDueDateUtc] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [setUpReminder, setSetUpReminder] = useState(true)
  const [reminderMinutes, setReminderMinutes] = useState<number | ''>(60)
  const [assignedTo, setAssignedTo] = useState('')
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([])
  const [showContactsPopup, setShowContactsPopup] = useState(false)
  const [formTouched, setFormTouched] = useState(false)
  const [touchedFields, setTouchedFields] = useState<{title: boolean, dueDate: boolean, reminderMinutesBeforeDue: boolean}>({
    title: false,
    dueDate: false,
    reminderMinutesBeforeDue: false,
  })

  const Teamlist = useSelector((state: any) => {
    const teamData = state?.teamlist?.TeamList?.items || []
    const user = state?.userDetails?.userDetails?.data?.user
    if (!user) return teamData
    return [user, ...teamData.filter((member: any) => member?._id !== user?._id)]
  })
  const currentUser = useSelector((state: any) => state?.userDetails?.userDetails?.data?.user)

  const effectiveTimeZone = useMemo(() => {
    const assigned = Teamlist?.find((m: any) => m?._id === assignedTo)
    const candidate =
      assigned?.timeZone ||
      assigned?.timezone ||
      assigned?.tz ||
      currentUser?.timeZone ||
      currentUser?.timezone ||
      currentUser?.tz ||
      ''
    return resolveIanaTimeZone(candidate)
  }, [Teamlist, assignedTo, currentUser])

  useEffect(() => {
    if (show) {
      if (initialTask) {
        setTitle(initialTask.title || '')
        setDescription(initialTask.description || '')
        const assigneeId = initialTask.assignedTo?._id || ''
        const assignedMember = Teamlist?.find((m: any) => m?._id === assigneeId)
        const initialTz = resolveIanaTimeZone(
          assignedMember?.timeZone ||
            assignedMember?.timezone ||
            assignedMember?.tz ||
            currentUser?.timeZone ||
            currentUser?.timezone ||
            currentUser?.tz
        )
        setDueDate(toDateTimeLocalValueInTz(initialTask.dueDate, initialTz))
        setDueDateUtc(initialTask.dueDate || '')
        setPriority((initialTask.priority as TaskPriority) || 'medium')
        setSetUpReminder(initialTask.setUpReminder ?? true)
        setReminderMinutes(initialTask.reminderMinutesBeforeDue ?? 60)
        setAssignedTo(assigneeId)
        const mappedContacts =
          initialTask.contacts?.map((contact) => ({
            id: contact.id,
            label: buildNameContact(contact),
          })) || []
        setSelectedContacts(mappedContacts.filter((item) => item.label))
      } else {
        setTitle('')
        setDescription('')
        setDueDate('')
        setDueDateUtc('')
        setPriority('medium')
        setSetUpReminder(true)
        setReminderMinutes(60)
        setAssignedTo('')
        setSelectedContacts([])
      }
      setFormTouched(false)
      setTouchedFields({ title: false, dueDate: false, reminderMinutesBeforeDue: false })
    }
  }, [show, initialTask])

  useEffect(() => {
    if (!dueDate) {
      setDueDateUtc('')
      return
    }
    const utcIso = toUtcIsoFromTimeZone(dueDate, effectiveTimeZone)
    if (utcIso) setDueDateUtc(utcIso)
  }, [dueDate, effectiveTimeZone])

  useEffect(() => {
    if (!dueDateUtc) return
    const next = toDateTimeLocalValueInTz(dueDateUtc, effectiveTimeZone)
    if (next && next !== dueDate) {
      setDueDate(next)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTimeZone])

  const isDueDateValid = useMemo(() => {
    if (!dueDate) return false
    const parsed = new Date(dueDate)
    return !Number.isNaN(parsed.getTime())
  }, [dueDate])

  const isReminderMinutesValid = useMemo(() => {
    if (!setUpReminder) return true // Not required if reminder is disabled
    if (reminderMinutes === '') return false
    if (typeof reminderMinutes !== 'number') return false
    return reminderMinutes >= 5
  }, [setUpReminder, reminderMinutes])

  const handleReminderMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target
    if (value === '') {
      setReminderMinutes('')
      return
    }
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
      return
    }
    setReminderMinutes(parsed)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setFormTouched(true)
    setTouchedFields({ title: true, dueDate: true, reminderMinutesBeforeDue: true })
    if (!title.trim() || !isDueDateValid || !isReminderMinutesValid) {
      return
    }

    const resolvedDueDateUtc =
      dueDateUtc || toUtcIsoFromTimeZone(dueDate, effectiveTimeZone)
    const payload: TaskFormValues = {
      title: title.trim(),
      description: description.trim() ? description.trim() : undefined,
      dueDate: resolvedDueDateUtc,
      priority,
      setUpReminder,
      reminderMinutesBeforeDue: setUpReminder
        ? typeof reminderMinutes === 'number'
          ? reminderMinutes
          : 60
        : undefined,
      assignedTo: assignedTo.trim() ? assignedTo.trim() : undefined,
      contacts: selectedContacts.map((contact) => contact.id),
    }

    try {
      await onSubmit(payload)
      // Only close if onSubmit succeeds (it will be handled by parent, but we reset formTouched here)
      setFormTouched(false)
      setTouchedFields({ title: false, dueDate: false, reminderMinutesBeforeDue: false })
    } catch (error) {
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered backdrop='static'>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Header closeButton>
          <div className="d-flex w-100">
            <span
              className="me-auto text-nowrap fw-bold fs-3"
            >
              {initialTask && (initialTask.id || initialTask._id) ? 'Edit Task' : 'Create Task'}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row className='g-4'>
            <Col xs={12}>
              <Form.Group controlId='taskTitle'>
                <Form.Label className='d-flex align-items-center gap-1'>
                  Title
                  <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='text'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={() => setTouchedFields(prev => ({ ...prev, title: true }))}
                  placeholder='Add a task title'
                  isInvalid={(formTouched || touchedFields.title) && !title.trim()}
                />
                {(formTouched || touchedFields.title) && !title.trim() && (
                  <Form.Control.Feedback type='invalid'>Title is required</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId='taskDescription'>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='Add any context or instructions for the assignee'
                />
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group controlId='taskDueDate'>
                <Form.Label className='d-flex align-items-center gap-1'>
                  Due Date
                  <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='datetime-local'
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  onBlur={() => setTouchedFields(prev => ({ ...prev, dueDate: true }))}
                  isInvalid={(formTouched || touchedFields.dueDate) && !isDueDateValid}
                />
                <Form.Text className='text-muted'>
                  Times shown in {effectiveTimeZone} (assignee&apos;s timezone)
                </Form.Text>
                {(formTouched || touchedFields.dueDate) && !isDueDateValid && (
                  <Form.Control.Feedback type='invalid'>Due date is required</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group controlId='taskPriority'>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group controlId='taskAssigned'>
                <Form.Label>Assign To</Form.Label>
                <Form.Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                  {Teamlist?.map((member: any) => (
                    <option key={member?._id} value={member?._id}>
                      {member?.name || member?.fullName || member?.email || member?._id}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group controlId='taskReminder'>
                <Form.Label className='d-flex align-items-center gap-1'>
                  Reminder
                  {setUpReminder && (
                    <span className='text-danger'>*</span>
                  )}
                </Form.Label>
                <div className='d-flex align-items-center gap-2'>
                  <Form.Check
                    type='switch'
                    id='taskReminderToggle'
                    label={setUpReminder ? 'Enabled' : 'Disabled'}
                    checked={setUpReminder}
                    onChange={(e) => setSetUpReminder(e.target.checked)}
                    className='task-reminder-toggle'
                  />
                  {setUpReminder && (
                    <>
                      <Form.Control
                        type='number'
                        min={5}
                        step={5}
                        value={reminderMinutes === '' ? '' : reminderMinutes}
                        onChange={handleReminderMinutesChange}
                        onBlur={() => setTouchedFields(prev => ({ ...prev, reminderMinutesBeforeDue: true }))}
                        isInvalid={(formTouched || touchedFields.reminderMinutesBeforeDue) && !isReminderMinutesValid}
                        style={{maxWidth: '140px'}}
                      />
                      <span className='text-muted'>minutes before due</span>
                    </>
                  )}
                </div>
                {setUpReminder && (formTouched || touchedFields.reminderMinutesBeforeDue) && !isReminderMinutesValid && (
                  <Form.Control.Feedback type='invalid' className='d-block'>
                    Reminder minutes must be at least 5
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group controlId='taskContacts'>
                <Form.Label>Linked Contacts</Form.Label>
                <div className='d-flex flex-wrap gap-2 mb-2'>
                  {selectedContacts.length === 0 && (
                    <span className='text-muted'>No contacts selected</span>
                  )}
                  {selectedContacts.map((contact) => (
                    <Badge
                      key={contact.id}
                      bg='light'
                      text='dark'
                      className='border fw-semibold d-inline-flex align-items-center gap-2'
                    >
                      {contact.label}
                      <i
                        className='bi bi-x-circle cursor-pointer'
                        onClick={() =>
                          setSelectedContacts((prev) => prev.filter((c) => c.id !== contact.id))
                        }
                        style={{cursor: 'pointer'}}
                      ></i>
                    </Badge>
                  ))}
                </div>
                <Button
                  variant='light'
                  className='btn btn-light-primary'
                  onClick={() => setShowContactsPopup(true)}
                >
                  <i className='bi bi-person-plus me-2'></i>
                  Select Contacts
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : initialTask && (initialTask.id || initialTask._id) ? 'Update Task' : 'Create Task'}
          </Button>
        </Modal.Footer>
      </Form>
      <QMContactListPopup
        show={showContactsPopup}
        handleClose={() => setShowContactsPopup(false)}
        status={ContactStatus.subscribe}
        returnFullContactData={true}
        selectedCallback={(contactsData: any[]) => {
          const newContacts: SelectedContact[] = contactsData.map((contact) => {
            const fullName = buildNameContact(contact)
            return {
              id: contact.id,
              label: fullName || contact.number,
            }
          })

          setSelectedContacts((prev) => {
            const existingIds = new Set(prev.map((c) => c.id))
            const uniqueNewContacts = newContacts.filter((c) => !existingIds.has(c.id))
            return [...prev, ...uniqueNewContacts]
          })
          setShowContactsPopup(false)
        }}
      />
    </Modal>
  )
}

export default TaskFormModal
